import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { MessageSquare, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { API_URL, analytics } from "@/lib/utils";
import { getLocationDisplayName, LOCATION_FORM_ORDER } from "@/lib/locationUrls";
import { SEO } from "@/components/SEO";
import { TurnstileWidget } from "@/components/TurnstileWidget";

export default function FeedbackPage() {
  const [locations, setLocations] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  const pageLoadTimeRef = useRef(Date.now());
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    location_id: "",
    current_member: "",
    message: "",
    website: "" // honeypot
  });

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API_URL}/locations`);
      setLocations(response.data);
    } catch (error) {
      console.error("Failed to fetch locations:", error);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.website) return; // honeypot
    if (!turnstileToken) {
      toast.error("Please complete the verification");
      return;
    }

    if (!formData.name?.trim() || !formData.current_member || !formData.message?.trim()) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API_URL}/feedback`, { ...formData, time_on_page: Date.now() - pageLoadTimeRef.current, "cf-turnstile-response": turnstileToken });
      analytics.trackFeedbackSubmit();
      setIsSubmitted(true);
      toast.success("Feedback submitted successfully!");
    } catch (error) {
      console.error("Failed to submit feedback:", error);
      toast.error("Failed to submit feedback. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center px-4" data-testid="feedback-success">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="text-center max-w-md"
        >
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-brand-rose/20 flex items-center justify-center">
            <CheckCircle className="w-10 h-10 text-brand-rose" />
          </div>
          <h2 className="text-3xl font-heading font-bold text-white mb-4">THANK YOU!</h2>
          <p className="text-gray-700 mb-8">
            We appreciate your feedback. Your input helps us improve and provide a better experience for all our members.
          </p>
          <Button 
            onClick={() => window.location.href = "/"}
            className="bg-brand-pink hover:bg-brand-pink-hover text-white font-bold uppercase tracking-wider"
          >
            Back to Home
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20" data-testid="feedback-page">
      <SEO
        title="Share Your Feedback"
        description="Share your feedback about Jersey Girls. Help us improve by sharing your experience at our locations. Your opinion matters to us."
        url="/feedback"
        noindex={true}
      />
      {/* Header */}
      <section className="py-12 md:py-20 px-4 md:px-8">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-brand-rose/20 flex items-center justify-center">
              <MessageSquare className="w-8 h-8 text-brand-rose" />
            </div>
            <h1 className="text-4xl md:text-6xl font-heading font-black text-gray-900 mb-4" data-testid="feedback-heading">
              SHARE YOUR FEEDBACK
            </h1>
            <p className="text-gray-700 text-lg">
              Your opinion matters to us. Help us improve by sharing your experience.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Form Section */}
      <section className="pb-20 px-4 md:px-8">
        <div className="max-w-xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="feedback-form">
              <div className="form-group absolute -left-[9999px] w-1 h-1 overflow-hidden" aria-hidden="true">
                <Label htmlFor="feedback-website">Website</Label>
                <Input id="feedback-website" name="website" type="text" value={formData.website} onChange={handleChange} tabIndex={-1} autoComplete="off" />
              </div>
              <div className="form-group">
                <Label htmlFor="name" className="form-label">Name *</Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="h-12 bg-white border-gray-300 focus:border-brand-pink text-gray-900"
                  placeholder="Your name"
                  data-testid="feedback-name-input"
                />
              </div>

              <div className="form-group">
                <Label htmlFor="email" className="form-label">Email (Optional)</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="h-12 bg-white border-gray-300 focus:border-brand-pink text-gray-900"
                  placeholder="your@email.com"
                  data-testid="feedback-email-input"
                />
              </div>

              <div className="form-group">
                <Label className="form-label">Location (Optional)</Label>
                <Select
                  value={formData.location_id}
                  onValueChange={(value) => setFormData({ ...formData, location_id: value })}
                >
                  <SelectTrigger className="h-12 bg-white border-gray-300 focus:border-brand-pink text-gray-900" data-testid="feedback-location-select">
                    <SelectValue placeholder="Select a location (optional)" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">
                    {[...locations]
                      .sort((a, b) => LOCATION_FORM_ORDER.indexOf(a.id) - LOCATION_FORM_ORDER.indexOf(b.id))
                      .map((loc) => (
                        <SelectItem key={loc.id} value={loc.id} className="text-gray-900 hover:bg-gray-100">
                          {getLocationDisplayName(loc.id) ?? loc.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="form-group">
                <Label className="form-label">Current Member? *</Label>
                <Select
                  value={formData.current_member}
                  onValueChange={(value) => setFormData({ ...formData, current_member: value })}
                >
                  <SelectTrigger className="h-12 bg-white border-gray-300 focus:border-brand-pink text-gray-900" data-testid="feedback-current-member-select">
                    <SelectValue placeholder="Yes or No" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">
                    <SelectItem value="Yes" className="text-gray-900 hover:bg-gray-100">Yes</SelectItem>
                    <SelectItem value="No" className="text-gray-900 hover:bg-gray-100">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="form-group">
                <Label htmlFor="message" className="form-label">Your Feedback *</Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  className="bg-white border-gray-300 focus:border-brand-pink text-gray-900 resize-none"
                  placeholder="Tell us about your experience..."
                  data-testid="feedback-message-input"
                />
              </div>

              <div className="form-group">
                <TurnstileWidget onVerify={setTurnstileToken} onExpire={() => setTurnstileToken("")} theme="light" />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting || !turnstileToken}
                className="w-full h-12 bg-brand-rose hover:bg-brand-rose-hover text-white font-bold uppercase tracking-wider"
                data-testid="feedback-submit-btn"
              >
                {isSubmitting ? (
                  <span className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                    Submitting...
                  </span>
                ) : (
                  <span className="flex items-center">
                    <MessageSquare className="w-4 h-4 mr-2" />
                    Submit Feedback
                  </span>
                )}
              </Button>
            </form>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
